/**
 * Driver enablement in the Admin (W8-DRIVER-ELIGIBILITY, D437, ADR-0069) — framework-neutral view-models for the three
 * screens over the `driver-eligibility-routes.ts` surface:
 *   - **Configurações › Documentos exigidos** (`kyc_catalog`): a country's KYC document-type catalog under four eyes — the
 *     version in force, the pending change (who proposed it, why, when), approve / reject / withdraw, the history, and a
 *     draft EDITOR that starts from the version in force, is validated by the kernel's own `validateKycCatalog` before it
 *     is proposed, and is sent whole as the next version;
 *   - **Motoristas › Documentos** (`driver_documents`): the assisted IN-PERSON review — the market's drivers and where
 *     each stands (per category and as a courier), one driver's document lines, and one review line per type;
 *   - **Motoristas › Veículos** (`driver_vehicles`): the vehicles the market's drivers registered, their latest
 *     inspection and the operator inspection (categories chosen FROM the catalog's list, never typed).
 *
 * The console only FILTERS and EXPLAINS: it hides Approve from the proposer (with the reason shown) and write actions from
 * whoever lacks the word, and it translates every refusal into words. The server is the authority on all of it — the
 * identities it compares are the sessions', never a body field. Neutral: every document code, label, category and
 * market is data; no country, document or language literal lives here.
 */
import {
  type ApiError,
  type DocumentReviewInput,
  type DriverDocumentsAdminView,
  type DriverEnablementAdminApi,
  type EligibilityOverviewView,
  type KycCatalogAdminApi,
  type KycCatalogVersionView,
  type KycCatalogView,
  type KycCatalogWire,
  type KycDocumentTypeWire,
  type KycRequirementBindingWire,
  type KycVehicleRuleWire,
  type MarketDriverStanding,
  type MarketVehiclesView,
  type ReviewedDocumentView,
  type VehicleInspectionInput,
} from '@vello/api-client';
import { type Result } from '@vello/core';
import {
  INSPECTION_VEHICLE_CLASSES,
  KYC_REVIEW_APPROVE_REASONS,
  KYC_REVIEW_REJECT_REASONS,
  VEHICLE_INSPECTION_FAIL_REASONS,
  VEHICLE_INSPECTION_PASS_REASONS,
  hashKycCatalog,
  kycTypeLabel,
  validateKycCatalog,
  validityEndFromIssue,
  type KycCatalogProblemCode,
} from '@vello/driver-eligibility';
import { grantsAllow, type KnownGrants } from './console-access.js';
import { isValidTimeZone, marketDayEnd } from './market-time.js';

export type KycCatalogRuntime = { readonly kycCatalog: KycCatalogAdminApi };
export type DriverEnablementRuntime = { readonly driverEnablement: DriverEnablementAdminApi };

export type EnablementTone = 'neutral' | 'success' | 'warning' | 'danger';

/** Ids are uuids the server compares case- and space-insensitively (like the m0137 CHECK). */
const sameId = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

// ---- authority (display only — the server re-authorizes every call) --------------------------------------------------

/**
 * The catalog words are GEO on the server, authorized at the catalog's COUNTRY: a GLOBAL or COUNTRY grant covers them, a
 * CITY grant never does (a city operator does not govern a country's documents).
 */
export function kycCatalogAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayPropose: boolean; readonly mayApprove: boolean } {
  if (grants === undefined) return { mayView: true, mayPropose: true, mayApprove: true };
  return {
    mayView: grantsAllow(grants, { permission: 'kyc_catalog:view', target: 'COUNTRY' }),
    mayPropose: grantsAllow(grants, { permission: 'kyc_catalog:propose', target: 'COUNTRY' }),
    mayApprove: grantsAllow(grants, { permission: 'kyc_catalog:approve', target: 'COUNTRY' }),
  };
}

/**
 * The market reads and the inspection are GEO (a CITY grant of that market works them); the in-person review is the KYC
 * reviewer authority — GLOBAL or the driver's COUNTRY, never a CITY.
 */
export function driverEnablementAuthority(grants: KnownGrants): { readonly mayViewDocuments: boolean; readonly mayReview: boolean; readonly mayViewVehicles: boolean; readonly mayInspect: boolean } {
  if (grants === undefined) return { mayViewDocuments: true, mayReview: true, mayViewVehicles: true, mayInspect: true };
  return {
    mayViewDocuments: grantsAllow(grants, { permission: 'document:view', target: 'GEO' }),
    mayReview: grantsAllow(grants, { permission: 'document:review', target: 'COUNTRY' }),
    mayViewVehicles: grantsAllow(grants, { permission: 'vehicle:view', target: 'GEO' }),
    mayInspect: grantsAllow(grants, { permission: 'vehicle:manage', target: 'GEO' }),
  };
}

// ---- the catalog: four eyes ---------------------------------------------------------------------------------------------

/** Why Approve is NOT offered on a pending change, or undefined when it is. Display only. */
export type KycCatalogApproveBlock = 'NOT_PENDING' | 'OWN_PROPOSAL' | 'NO_PERMISSION';

export function kycCatalogApproveBlock(change: Pick<KycCatalogVersionView, 'state' | 'proposedBy'>, operatorId: string | undefined, mayApprove: boolean, selfApproval = false): KycCatalogApproveBlock | undefined {
  if (change.state !== 'PENDING') return 'NOT_PENDING';
  if (!selfApproval && operatorId !== undefined && sameId(change.proposedBy, operatorId)) return 'OWN_PROPOSAL';
  if (!mayApprove) return 'NO_PERMISSION';
  return undefined;
}

/** The proposer withdraws its own pending change (the server's `NOT_PROPOSER` for anyone else). */
export function kycCatalogWithdrawable(change: Pick<KycCatalogVersionView, 'state' | 'proposedBy'>, operatorId: string | undefined, mayPropose: boolean): boolean {
  return mayPropose && change.state === 'PENDING' && operatorId !== undefined && sameId(change.proposedBy, operatorId);
}

/** A checker rejects someone else's pending change (the proposer withdraws its own instead). */
export function kycCatalogRejectable(change: Pick<KycCatalogVersionView, 'state' | 'proposedBy'>, operatorId: string | undefined, mayApprove: boolean): boolean {
  return mayApprove && change.state === 'PENDING' && !(operatorId !== undefined && sameId(change.proposedBy, operatorId));
}

export function kycCatalogStateTone(state: string): EnablementTone {
  return state === 'APPROVED' ? 'success' : state === 'PENDING' ? 'warning' : state === 'REJECTED' ? 'danger' : 'neutral';
}

/**
 * The countries the catalog screen offers: the countries of the markets the operator's scope reaches, the scoped country,
 * and (for a GLOBAL operator, whose `GET countries` answers) the countries that already have a catalog. Upper-case alpha-2,
 * sorted, never typed.
 */
export function kycCatalogCountryChoices(marketIds: readonly string[], scopeCountry: string | undefined, listed: readonly string[]): readonly string[] {
  const out = new Set<string>();
  for (const id of marketIds) if (/^[a-z]{2}:/.test(id)) out.add(id.slice(0, 2).toUpperCase());
  if (scopeCountry !== undefined && /^[A-Za-z]{2}$/.test(scopeCountry)) out.add(scopeCountry.toUpperCase());
  for (const c of listed) if (/^[A-Z]{2}$/.test(c)) out.add(c);
  return [...out].sort();
}

// ---- refusals and reasons in words ------------------------------------------------------------------------------------

/** The eligibility reason codes an operator is shown in words (`DOCUMENT_*` carry the type after a colon). */
export const ELIGIBILITY_REASON_CODES = [
  'DOCUMENT_MISSING',
  'DOCUMENT_EXPIRED',
  'DOCUMENT_REJECTED',
  'DOCUMENT_PENDING_REVIEW',
  'DOCUMENT_EXPIRY_UNKNOWN',
  'VEHICLE_NOT_INSPECTED',
  'VEHICLE_CATEGORY_MISMATCH',
  'VEHICLE_INSPECTION_EXPIRED',
  'VEHICLE_NOT_ACTIVE',
  'VEHICLE_RULE_NOT_SET',
  'VEHICLE_TOO_OLD',
  'KYC_CATALOG_NOT_SET',
  'KYC_REQUIREMENTS_NOT_DEFINED',
  'DRIVER_RATING_TOO_LOW',
  'DRIVER_TRIPS_TOO_FEW',
] as const;

/**
 * One eligibility reason, split for the screen: the message key (undefined for a code the console does not know — it is
 * then shown raw rather than hidden) and the document type a `DOCUMENT_*:<TYPE>` reason names.
 */
export function eligibilityReason(reason: string): { readonly key: string | undefined; readonly type?: string; readonly raw: string } {
  const at = reason.indexOf(':');
  const code = at < 0 ? reason : reason.slice(0, at);
  const type = at < 0 ? undefined : reason.slice(at + 1);
  const key = (ELIGIBILITY_REASON_CODES as readonly string[]).includes(code) ? `denable.reason.${code}` : undefined;
  return { key, ...(type !== undefined && type !== '' ? { type } : {}), raw: reason };
}

/**
 * A document type's label from the catalog types a response carries. A type the list does not hold reads as `unregistered` — the
 * screen's catalogued words (`kyc.document_type_fallback`, D588) — never as the stored code.
 */
export function documentTypeLabel(types: readonly Pick<KycDocumentTypeWire, 'code' | 'labels'>[], code: string, lang: string, unregistered: string): string {
  const type = types.find((t) => t.code === code);
  return type !== undefined ? kycTypeLabel(type, lang) : unregistered;
}

/** One line per service context of an overview: every catalog category, then the courier. */
export interface EligibilityRow {
  readonly key: string;
  readonly kind: 'category' | 'courier';
  readonly category?: string;
  readonly eligible: boolean;
  readonly reasons: readonly string[];
}

export function eligibilityRows(overview: Pick<EligibilityOverviewView, 'categories' | 'courier'>): readonly EligibilityRow[] {
  return [
    ...overview.categories.map((c) => ({ key: `category:${c.category}`, kind: 'category' as const, category: c.category, eligible: c.eligible, reasons: c.reasons })),
    { key: 'courier', kind: 'courier' as const, eligible: overview.courier.eligible, reasons: overview.courier.reasons },
  ];
}

export function documentStandingTone(status: string): EnablementTone {
  return status === 'OK' ? 'success' : status === 'PENDING_REVIEW' || status === 'EXPIRY_UNKNOWN' ? 'warning' : status === 'MISSING' || status === 'EXPIRED' || status === 'REJECTED' ? 'danger' : 'neutral';
}

export function reviewLineTone(status: string): EnablementTone {
  return status === 'APPROVED' ? 'success' : status === 'REJECTED' || status === 'EXPIRED' ? 'danger' : status === 'PENDING_REVIEW' ? 'warning' : 'neutral';
}

export function vehicleStatusTone(status: string): EnablementTone {
  return status === 'ACTIVE' ? 'success' : status === 'PENDING_INSPECTION' ? 'warning' : 'neutral';
}

// ---- the in-person review line ----------------------------------------------------------------------------------------

export const DOCUMENT_REVIEW_RESULTS = ['APPROVED', 'REJECTED'] as const;
export type DocumentReviewResult = (typeof DOCUMENT_REVIEW_RESULTS)[number];

/** The reason catalog of a review result (approve: the operator saw the original; reject: why not). */
export function documentReasonOptions(result: DocumentReviewResult): readonly string[] {
  return result === 'APPROVED' ? KYC_REVIEW_APPROVE_REASONS : KYC_REVIEW_REJECT_REASONS;
}

/** What the operator types on one review line. `expiryDate` is the date input's `YYYY-MM-DD`. */
export interface DocumentLineDraft {
  readonly result: DocumentReviewResult;
  readonly number: string;
  readonly expiryDate: string;
  /** The issue date read on a document that carries no expiry (owner answer, PR 291 question 4); `''` until typed. */
  readonly issueDate: string;
  readonly reasonCode: string;
  readonly note: string;
  readonly vehicleId: string;
}

export function emptyDocumentLineDraft(): DocumentLineDraft {
  return { result: 'APPROVED', number: '', expiryDate: '', issueDate: '', reasonCode: KYC_REVIEW_APPROVE_REASONS[0], note: '', vehicleId: '' };
}

/** Switching the result resets the reason to that result's first option (a reason never crosses results). */
export function withDocumentResult(draft: DocumentLineDraft, result: DocumentReviewResult): DocumentLineDraft {
  return { ...draft, result, reasonCode: documentReasonOptions(result)[0] ?? '' };
}

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A calendar date (`YYYY-MM-DD`) as the last instant of that day on the MARKET's wall clock, in UTC (review W8E-02): a
 * validity printed on a document is a day where the document is used, so "valid until that day" ends at the market's
 * local midnight — never at UTC midnight (3 h early in a UTC−3 market, up to 14 h late east of UTC). Undefined for an
 * empty or impossible date, or when the market's time zone is unknown (the caller then sends the date as typed and the
 * server refuses it in words — never read as UTC, never dropped).
 */
export function endOfMarketDay(date: string, timeZone: string | undefined): string | undefined {
  const m = DATE.exec(date.trim());
  if (m === null || !isValidTimeZone(timeZone)) return undefined;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) return undefined;
  return marketDayEnd(date.trim(), timeZone);
}

/** The instant a typed validity date is sent as: the end of the market day, else the date as typed (the server refuses it). */
function validityInstant(date: string, timeZone: string | undefined): string | undefined {
  if (date.trim() === '') return undefined;
  return endOfMarketDay(date, timeZone) ?? date.trim();
}

/**
 * The body of one review line. Nothing is invented: an empty number, date or note is left out, so the server's own
 * validation answers (`NUMBER_REQUIRED`, `EXPIRY_REQUIRED`, `NOTE_REQUIRED`) and the screen explains it in words. The
 * vehicle is sent only for a VEHICLE type.
 */
export function documentReviewInput(type: Pick<KycDocumentTypeWire, 'code' | 'subject'>, draft: DocumentLineDraft, timeZone: string | undefined): DocumentReviewInput {
  const number = draft.number.trim();
  const expiresAt = validityInstant(draft.expiryDate, timeZone);
  const issuedOn = draft.issueDate.trim();
  const note = draft.note.trim();
  const vehicleId = draft.vehicleId.trim();
  return {
    documentType: type.code,
    result: draft.result,
    reasonCode: draft.reasonCode,
    ...(number !== '' ? { number } : {}),
    ...(expiresAt !== undefined ? { expiresAt } : {}),
    // The server counts the type's validity from the issue date in the market zone when no expiry is printed.
    ...(issuedOn !== '' ? { issuedOn } : {}),
    ...(note !== '' ? { note } : {}),
    ...(type.subject === 'VEHICLE' && vehicleId !== '' ? { vehicleId } : {}),
  };
}

/**
 * What the line will be valid until, shown before it is recorded (display only — the server computes the same with the
 * kernel): a printed expiry typed → the end of that day in the market zone; else an issue date on a type with a
 * `validityFromIssue` → the end of the day `issue + period` there (owner answer, PR 291 question 4); else undefined.
 */
export function documentValidityPreview(type: Pick<KycDocumentTypeWire, 'validityFromIssue'>, draft: Pick<DocumentLineDraft, 'expiryDate' | 'issueDate'>, timeZone: string | undefined): string | undefined {
  if (draft.expiryDate.trim() !== '') return endOfMarketDay(draft.expiryDate, timeZone);
  if (draft.issueDate.trim() === '' || type.validityFromIssue === undefined) return undefined;
  return validityEndFromIssue(draft.issueDate.trim(), type.validityFromIssue, timeZone);
}

/** The newest review line of each document type (the one that decides). */
export function latestLineByType(lines: readonly ReviewedDocumentView[]): ReadonlyMap<string, ReviewedDocumentView> {
  const out = new Map<string, ReviewedDocumentView>();
  for (const line of lines) {
    const prev = out.get(line.type);
    if (prev === undefined || Date.parse(line.createdAt) > Date.parse(prev.createdAt)) out.set(line.type, line);
  }
  return out;
}

/** The catalog types of a driver's documents view, those required in its market first (catalog order within each). */
export function reviewableTypes(view: Pick<DriverDocumentsAdminView, 'types'>): readonly (KycDocumentTypeWire & { readonly requiredHere: boolean })[] {
  return [...view.types.filter((t) => t.requiredHere), ...view.types.filter((t) => !t.requiredHere)];
}

// ---- the vehicle inspection -------------------------------------------------------------------------------------------

export const VEHICLE_INSPECTION_RESULTS = ['PASSED', 'FAILED'] as const;
export type VehicleInspectionResult = (typeof VEHICLE_INSPECTION_RESULTS)[number];

/** The vehicle statuses the list filters by; `''` = every status. */
export const DRIVER_VEHICLE_STATUSES = ['PENDING_INSPECTION', 'ACTIVE', 'INACTIVE', 'RETIRED'] as const;

export function inspectionReasonOptions(result: VehicleInspectionResult): readonly string[] {
  return result === 'PASSED' ? VEHICLE_INSPECTION_PASS_REASONS : VEHICLE_INSPECTION_FAIL_REASONS;
}

/**
 * The physical vehicle classes an inspection verifies (the kernel's list): the driver's typed vehicle type is free text,
 * so the class the operator saw is what dispatch uses. A PASSED inspection needs one.
 */
export const ADMIN_INSPECTION_VEHICLE_CLASSES: readonly string[] = INSPECTION_VEHICLE_CLASSES;

export interface InspectionDraft {
  readonly result: VehicleInspectionResult;
  readonly categories: readonly string[];
  /** The class the operator verified; `''` until chosen. */
  readonly vehicleClass: string;
  readonly plate: string;
  readonly reasonCode: string;
  readonly note: string;
  readonly validUntilDate: string;
}

/**
 * An empty inspection of a vehicle: the verified class is preselected from the driver's typed type only when it IS one of
 * the classes (never guessed from a free-text value that merely resembles one).
 */
export function emptyInspectionDraft(vehicle?: { readonly type: string }): InspectionDraft {
  const typed = vehicle?.type.trim().toLowerCase() ?? '';
  return {
    result: 'PASSED',
    categories: [],
    vehicleClass: ADMIN_INSPECTION_VEHICLE_CLASSES.includes(typed) ? typed : '',
    plate: '',
    reasonCode: VEHICLE_INSPECTION_PASS_REASONS[0],
    note: '',
    validUntilDate: '',
  };
}

export function withInspectionResult(draft: InspectionDraft, result: VehicleInspectionResult): InspectionDraft {
  return { ...draft, result, reasonCode: inspectionReasonOptions(result)[0] ?? '' };
}

/** Tick or untick one category — only the categories the market's catalog lists are ever offered. */
export function toggleInspectionCategory(draft: InspectionDraft, category: string, on: boolean): InspectionDraft {
  const rest = draft.categories.filter((c) => c !== category);
  return { ...draft, categories: on ? [...rest, category].sort() : rest };
}

/**
 * Whether the one primary action may be offered: the plate read on the vehicle, a reason, a note for `OTHER`, and — for a
 * pass — at least one category and the verified class. Display only — the server validates the same line.
 */
export function inspectionDraftReady(draft: InspectionDraft): boolean {
  if (draft.plate.trim() === '' || draft.reasonCode === '') return false;
  if (draft.reasonCode === 'OTHER' && draft.note.trim() === '') return false;
  if (draft.result === 'PASSED' && (draft.categories.length === 0 || draft.vehicleClass === '')) return false;
  return true;
}

export function vehicleInspectionInput(marketId: string, draft: InspectionDraft, timeZone: string | undefined): VehicleInspectionInput {
  const note = draft.note.trim();
  const validUntil = validityInstant(draft.validUntilDate, timeZone);
  return {
    marketId,
    result: draft.result,
    ...(draft.result === 'PASSED' ? { categories: [...draft.categories] } : {}),
    ...(draft.vehicleClass !== '' ? { vehicleClass: draft.vehicleClass } : {}),
    plate: draft.plate.trim(),
    reasonCode: draft.reasonCode,
    ...(note !== '' ? { note } : {}),
    ...(validUntil !== undefined ? { validUntil } : {}),
  };
}

// ---- the catalog editor (a draft of the next version) -----------------------------------------------------------------

/** A country with no catalog yet starts from an empty one. */
export function emptyKycCatalog(country: string): KycCatalogWire {
  return { country: country.toUpperCase(), types: [], requirements: [], vehicleRules: [] };
}

/** The draft an edit starts from: a copy of the version in force, else an empty catalog of the country. */
export function catalogDraftFrom(view: Pick<KycCatalogView, 'country' | 'inForce'> | undefined, country: string): KycCatalogWire {
  const inForce = view?.inForce?.catalog;
  return inForce !== undefined ? (JSON.parse(JSON.stringify(inForce)) as KycCatalogWire) : emptyKycCatalog(country);
}

/** Whether the draft differs from the version in force (by the kernel's content hash — key order never matters). */
export function catalogDraftChanged(draft: KycCatalogWire, inForce: KycCatalogWire | undefined): boolean {
  if (inForce === undefined) return draft.types.length > 0 || draft.requirements.length > 0 || draft.vehicleRules.length > 0;
  return hashKycCatalog(draft) !== hashKycCatalog(inForce);
}

/** Every category the draft names (bindings and vehicle rules), sorted — the category choices of a binding. */
export function catalogDraftCategories(draft: KycCatalogWire): readonly string[] {
  const out = new Set<string>();
  for (const b of draft.requirements) if (b.category !== undefined) out.add(b.category);
  for (const r of draft.vehicleRules) out.add(r.category);
  return [...out].sort();
}

export interface CatalogDraftProblem {
  readonly code: KycCatalogProblemCode;
  readonly path: string;
  readonly section: 'types' | 'requirements' | 'vehicleRules' | 'catalog';
  readonly index?: number;
  readonly messageId: string;
}

/** Every problem of the draft (the kernel's own validation, the same one the server runs), each with its message id. */
export function catalogDraftProblems(draft: KycCatalogWire): readonly CatalogDraftProblem[] {
  return validateKycCatalog(draft).map((p) => {
    const m = /^(types|requirements|vehicleRules)\[(\d+)\]$/.exec(p.path);
    return {
      code: p.code,
      path: p.path,
      section: m !== null ? (m[1] as 'types' | 'requirements' | 'vehicleRules') : 'catalog',
      ...(m !== null ? { index: Number(m[2]) } : {}),
      messageId: `denable.problem.${p.code}`,
    };
  });
}

const putAt = <T>(list: readonly T[], item: T, index: number | undefined): readonly T[] =>
  index === undefined || index < 0 || index >= list.length ? [...list, item] : list.map((x, i) => (i === index ? item : x));
const dropAt = <T>(list: readonly T[], index: number): readonly T[] => list.filter((_, i) => i !== index);

/** The document-type form: the rules as four switches, the notice as typed digits, one label per language. */
export interface KycTypeForm {
  readonly code: string;
  readonly subject: 'PERSON' | 'VEHICLE';
  readonly number: boolean;
  readonly expiry: boolean;
  readonly frontBack: boolean;
  readonly photo: boolean;
  readonly noticeDays: string;
  /** With no expiry printed, the validity from the issue date (owner answer, PR 291 question 4): typed digits + unit. */
  readonly validityAmount: string;
  readonly validityUnit: 'day' | 'month';
  readonly labels: Readonly<Record<string, string>>;
  readonly active: boolean;
}

/** The label languages the editor offers: every language the catalog already labels in, plus the operator's own. */
export function catalogLabelLanguages(draft: KycCatalogWire, lang: string): readonly string[] {
  const out = new Set<string>([lang.slice(0, 2).toLowerCase()]);
  for (const t of draft.types) for (const l of Object.keys(t.labels)) out.add(l);
  return [...out].sort();
}

export function kycTypeForm(type: KycDocumentTypeWire | undefined, languages: readonly string[]): KycTypeForm {
  const labels: Record<string, string> = {};
  for (const l of languages) labels[l] = type?.labels[l] ?? '';
  for (const [l, text] of Object.entries(type?.labels ?? {})) labels[l] = text;
  return {
    code: type?.code ?? '',
    subject: type?.subject ?? 'PERSON',
    number: type?.rules.number ?? false,
    expiry: type?.rules.expiry ?? false,
    frontBack: type?.rules.frontBack ?? false,
    photo: type?.rules.photo ?? false,
    noticeDays: type?.expiryNoticeDays !== undefined ? String(type.expiryNoticeDays) : '',
    validityAmount: type?.validityFromIssue !== undefined ? String(type.validityFromIssue.amount) : '',
    validityUnit: type?.validityFromIssue?.unit ?? 'day',
    labels,
    active: type?.active ?? true,
  };
}

/**
 * The document type a form describes. Nothing is silently fixed: a notice that is not a whole number becomes `0`, which
 * the kernel's validation refuses in words (`TYPE_NOTICE_DAYS_INVALID`), and an empty label is simply left out.
 */
export function kycTypeFromForm(form: KycTypeForm): KycDocumentTypeWire {
  const labels: Record<string, string> = {};
  for (const [l, text] of Object.entries(form.labels)) if (text.trim() !== '') labels[l] = text.trim();
  const notice = form.noticeDays.trim();
  const validity = form.validityAmount.trim();
  return {
    code: form.code.trim().toUpperCase(),
    subject: form.subject,
    rules: { number: form.number, expiry: form.expiry, frontBack: form.frontBack, photo: form.photo },
    ...(notice !== '' ? { expiryNoticeDays: /^\d+$/.test(notice) ? Number(notice) : 0 } : {}),
    // Not a whole number → 0, which the kernel refuses in words (`TYPE_VALIDITY_FROM_ISSUE_INVALID`).
    ...(validity !== '' ? { validityFromIssue: { amount: /^\d+$/.test(validity) ? Number(validity) : 0, unit: form.validityUnit } } : {}),
    labels,
    active: form.active,
  };
}

export function draftPutType(draft: KycCatalogWire, type: KycDocumentTypeWire, index?: number): KycCatalogWire {
  return { ...draft, types: putAt(draft.types, type, index) };
}

/** The requirement-binding form (`marketId` only at the market scope, `overridable` only at the country scope). */
export interface KycBindingForm {
  readonly documentType: string;
  readonly persona: 'driver' | 'courier';
  readonly scope: 'country' | 'market';
  readonly marketId: string;
  /** `''` = every category. */
  readonly category: string;
  readonly requirement: 'REQUIRED' | 'OPTIONAL';
  readonly overridable: boolean;
}

export function kycBindingForm(binding: KycRequirementBindingWire | undefined, defaultType = ''): KycBindingForm {
  return {
    documentType: binding?.documentType ?? defaultType,
    persona: binding?.persona ?? 'driver',
    scope: binding?.scope ?? 'country',
    marketId: binding?.marketId ?? '',
    category: binding?.category ?? '',
    requirement: binding?.requirement ?? 'REQUIRED',
    overridable: binding?.overridable ?? false,
  };
}

export function kycBindingFromForm(form: KycBindingForm): KycRequirementBindingWire {
  return {
    documentType: form.documentType,
    persona: form.persona,
    scope: form.scope,
    ...(form.scope === 'market' ? { marketId: form.marketId } : {}),
    ...(form.category.trim() !== '' ? { category: form.category.trim() } : {}),
    requirement: form.requirement,
    ...(form.scope === 'country' && form.overridable ? { overridable: true } : {}),
  };
}

export function draftPutBinding(draft: KycCatalogWire, binding: KycRequirementBindingWire, index?: number): KycCatalogWire {
  return { ...draft, requirements: putAt(draft.requirements, binding, index) };
}

export function draftRemoveBinding(draft: KycCatalogWire, index: number): KycCatalogWire {
  return { ...draft, requirements: dropAt(draft.requirements, index) };
}

/** The per-category vehicle-rule form: the vehicle classes as comma-separated tokens, the minimum model year as digits. */
export interface KycVehicleRuleForm {
  readonly category: string;
  readonly allowedVehicleTypes: string;
  readonly minVehicleYear: string;
  readonly requireValidInspection: boolean;
  readonly requireActiveVehicle: boolean;
}

export function kycVehicleRuleForm(rule: KycVehicleRuleWire | undefined): KycVehicleRuleForm {
  return {
    category: rule?.category ?? '',
    allowedVehicleTypes: (rule?.rule.allowedVehicleTypes ?? []).join(', '),
    minVehicleYear: rule?.rule.minVehicleYear !== undefined ? String(rule.rule.minVehicleYear) : '',
    requireValidInspection: rule?.rule.requireValidInspection ?? true,
    requireActiveVehicle: rule?.rule.requireActiveVehicle ?? true,
  };
}

/**
 * The rule a form describes, keeping every criterion the form does not edit (rating, trips, approved driver) from the rule
 * it started from. A year that is not a whole number becomes `0`, which the kernel refuses in words.
 */
export function kycVehicleRuleFromForm(form: KycVehicleRuleForm, base?: KycVehicleRuleWire): KycVehicleRuleWire {
  const classes = form.allowedVehicleTypes.split(',').map((s) => s.trim()).filter((s) => s !== '');
  const year = form.minVehicleYear.trim();
  const kept: Record<string, unknown> = { ...(base?.rule ?? {}) };
  for (const edited of ['allowedVehicleTypes', 'minVehicleYear', 'requireValidInspection', 'requireActiveVehicle']) delete kept[edited];
  return {
    category: form.category.trim(),
    rule: {
      ...(kept as KycVehicleRuleWire['rule']),
      ...(classes.length > 0 ? { allowedVehicleTypes: classes } : {}),
      ...(year !== '' ? { minVehicleYear: /^\d+$/.test(year) ? Number(year) : 0 } : {}),
      requireValidInspection: form.requireValidInspection,
      requireActiveVehicle: form.requireActiveVehicle,
    },
  };
}

export function draftPutVehicleRule(draft: KycCatalogWire, rule: KycVehicleRuleWire, index?: number): KycCatalogWire {
  return { ...draft, vehicleRules: putAt(draft.vehicleRules, rule, index) };
}

export function draftRemoveVehicleRule(draft: KycCatalogWire, index: number): KycCatalogWire {
  return { ...draft, vehicleRules: dropAt(draft.vehicleRules, index) };
}

// ---- loaders (the typed api groups) -----------------------------------------------------------------------------------

export function loadKycCatalog(runtime: KycCatalogRuntime, country: string): Promise<Result<KycCatalogView, ApiError>> {
  return runtime.kycCatalog.get(country.toUpperCase());
}

/** The countries that have a catalog (a GLOBAL read: a COUNTRY operator is refused, and the screen falls back to its scope). */
export function loadKycCatalogCountries(runtime: KycCatalogRuntime): Promise<Result<{ countries: readonly string[] }, ApiError>> {
  return runtime.kycCatalog.countries();
}

/** Propose the draft as the next version (the maker is the session), from the version in force it was edited on. */
export function proposeKycCatalog(runtime: KycCatalogRuntime, input: { readonly draft: KycCatalogWire; readonly baseVersion: number | undefined; readonly reason: string }): Promise<Result<KycCatalogVersionView, ApiError>> {
  return runtime.kycCatalog.propose({ country: input.draft.country, catalog: input.draft, ...(input.baseVersion !== undefined ? { baseVersion: input.baseVersion } : {}), reason: input.reason.trim() });
}

export type KycCatalogDecision = 'approve' | 'reject' | 'withdraw';

export function decideKycCatalog(runtime: KycCatalogRuntime, change: Pick<KycCatalogVersionView, 'country' | 'version'>, action: KycCatalogDecision, reason: string): Promise<Result<KycCatalogVersionView, ApiError>> {
  const r = reason.trim();
  return action === 'approve' ? runtime.kycCatalog.approve(change.country, change.version, r) : action === 'reject' ? runtime.kycCatalog.reject(change.country, change.version, r) : runtime.kycCatalog.withdraw(change.country, change.version, r);
}

export function loadMarketDrivers(runtime: DriverEnablementRuntime, marketId: string): Promise<Result<{ marketId: string; evaluatedAt: string; drivers: readonly MarketDriverStanding[] }, ApiError>> {
  return runtime.driverEnablement.marketDrivers(marketId);
}

export function loadDriverDocuments(runtime: DriverEnablementRuntime, driverId: string): Promise<Result<DriverDocumentsAdminView, ApiError>> {
  return runtime.driverEnablement.driverDocuments(driverId);
}

export function recordDocumentReview(runtime: DriverEnablementRuntime, driverId: string, line: DocumentReviewInput): Promise<Result<{ document: ReviewedDocumentView; eligibility: EligibilityOverviewView }, ApiError>> {
  return runtime.driverEnablement.reviewDocument(driverId, line);
}

export function loadMarketVehicles(runtime: DriverEnablementRuntime, marketId: string, status: string): Promise<Result<MarketVehiclesView, ApiError>> {
  return runtime.driverEnablement.vehicles(marketId, status !== '' ? { status } : {});
}

export function recordVehicleInspection(runtime: DriverEnablementRuntime, vehicleId: string, input: VehicleInspectionInput): ReturnType<DriverEnablementAdminApi['inspectVehicle']> {
  return runtime.driverEnablement.inspectVehicle(vehicleId, input);
}
